import React, { useState, useRef, useEffect } from "react";
import { X, Send, Sparkles } from "lucide-react";

const AI_URL = process.env.REACT_APP_AI_URL || "http://localhost:8000";

const WELCOME = {
  role: "assistant",
  content: "Bonjour! Je suis l'assistant IA d'aInnovation. Comment puis-je vous aider aujourd'hui?",
};

export default function ChatWidget() {
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    setMessages(p => [...p, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res  = await fetch(`${AI_URL}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: userMsg.content }) });
      const data = await res.json();
      setMessages(p => [...p, { role: "assistant", content: data.response }]);
    } catch {
      setMessages(p => [...p, { role: "assistant", content: "Je suis temporairement indisponible. Utilisez le formulaire de contact — nous répondons sous 24h." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: "fixed", bottom: "28px", right: "28px", zIndex: 200,
          width: 56, height: 56, borderRadius: "50%",
          background: open ? "#1d1d1f" : "linear-gradient(145deg, #0071e3, #0a84ff)",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: open ? "0 4px 16px rgba(0,0,0,0.25)" : "0 6px 24px rgba(0,113,227,0.40)",
          transition: "all 0.25s ease",
          transform: open ? "scale(1)" : "scale(1)",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.06)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
      >
        {open
          ? <X size={20} color="#fff" />
          : <Sparkles size={20} color="#fff" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: "fixed", bottom: "100px", right: "28px", zIndex: 200,
            width: "min(380px, calc(100vw - 40px))",
            background: "#fff",
            borderRadius: "20px",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.08)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div style={{ background: "linear-gradient(135deg, #0071e3, #0a84ff)", padding: "18px 20px", display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sparkles size={17} color="#fff" />
            </div>
            <div>
              <p style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 700, color: "#fff", fontSize: "0.9375rem" }}>Assistant aInnovation</p>
              <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "2px" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34c759" }} />
                <p style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.75)" }}>En ligne</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "10px", maxHeight: "320px", minHeight: "160px" }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div
                  style={{
                    maxWidth: "80%",
                    borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    padding: "10px 14px",
                    fontSize: "0.875rem",
                    lineHeight: 1.55,
                    background: m.role === "user" ? "#0071e3" : "#f5f5f7",
                    color: m.role === "user" ? "#fff" : "#1d1d1f",
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{ background: "#f5f5f7", borderRadius: "16px 16px 16px 4px", padding: "12px 16px", display: "flex", gap: "5px", alignItems: "center" }}>
                  {[0, 150, 300].map(d => (
                    <div key={d} style={{ width: 6, height: 6, borderRadius: "50%", background: "#6e6e73", animation: `bounce 1s ${d}ms infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <form onSubmit={send} style={{ padding: "12px 14px 14px", borderTop: "1px solid rgba(0,0,0,0.07)", display: "flex", gap: "8px" }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Posez votre question..."
              style={{ flex: 1, border: "1px solid rgba(0,0,0,0.10)", borderRadius: "999px", padding: "10px 16px", fontSize: "0.875rem", outline: "none", fontFamily: "'Inter'" }}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              style={{
                width: 40, height: 40, flexShrink: 0,
                background: "#0071e3", border: "none", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                opacity: input.trim() && !loading ? 1 : 0.4,
                transition: "opacity 0.2s",
              }}
            >
              <Send size={15} color="#fff" />
            </button>
          </form>
        </div>
      )}

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}
